import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Bell, Clock, CheckCircle, X, ChevronLeft, RefreshCcw } from 'lucide-react';

export const RegistrationRequestsContent = ({
  registrationRequests,
  approveRegistration,
  rejectRegistration,
  setActiveSection,
  loadData
}) => {
  const pendingRequests = registrationRequests.filter(r => r.status === 'pending');
  const approvedRequests = registrationRequests.filter(r => r.status === 'approved');
  const rejectedRequests = registrationRequests.filter(r => r.status === 'rejected');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => setActiveSection('student-hub')}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Geri
        </Button>
        <h2 className="text-2xl font-bold text-gray-800">Kayıt Talepleri</h2>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-yellow-500 to-orange-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm">Bekleyen</p>
                <p className="text-3xl font-bold">{pendingRequests.length}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-emerald-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Onaylanan</p>
                <p className="text-3xl font-bold">{approvedRequests.length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-red-500 to-rose-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Reddedilen</p>
                <p className="text-3xl font-bold">{rejectedRequests.length}</p>
              </div>
              <X className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bekleyen Talepler */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-500" />
            Bekleyen Kayıt Talepleri ({pendingRequests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
              <p className="text-gray-500">Bekleyen kayıt talebi yok</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map(request => (
                <div 
                  key={request.id} 
                  className="p-4 border rounded-xl bg-yellow-50 border-yellow-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white text-lg font-bold">
                        {request.first_name?.charAt(0)}{request.last_name?.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{request.name || `${request.first_name} ${request.last_name}`}</h3>
                        <p className="text-sm text-gray-600">{request.email}</p>
                        {request.phone && <p className="text-sm text-gray-500">{request.phone}</p>}
                        {request.counselor_name && (
                          <p className="text-xs text-purple-600 mt-1">Danışman: {request.counselor_name}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(request.created_at).toLocaleDateString('tr-TR', { 
                            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => approveRegistration(request.id)}
                        data-testid={`approve-${request.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Onayla
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => rejectRegistration(request.id)}
                        data-testid={`reject-${request.id}`}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reddet
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Geçmiş Talepler */}
      {(approvedRequests.length > 0 || rejectedRequests.length > 0) && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-500" />
              Geçmiş Talepler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {[...approvedRequests, ...rejectedRequests]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 20)
                .map(request => (
                  <div 
                    key={request.id} 
                    className={`p-3 rounded-lg flex items-center justify-between ${
                      request.status === 'approved' ? 'bg-green-50' : 'bg-red-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        request.status === 'approved' ? 'bg-green-500' : 'bg-red-500'
                      } text-white text-sm font-bold`}>
                        {request.first_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{request.name}</p>
                        <p className="text-xs text-gray-500">{request.email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      request.status === 'approved' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                    }`}>
                      {request.status === 'approved' ? 'Onaylandı' : 'Reddedildi'}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RegistrationRequestsContent;
